package gr.tech.pdfgenerator.infrastructure.api

import gr.tech.GRAttestation.models.api.ApiError
import gr.tech.GRAttestation.models.api.ApiResponse
import gr.tech.pdfgenerator.infrastructure.session.UserSession
import gr.tech.pdfgenerator.models.user.UserPreview
import gr.tech.pdfgenerator.navigation.SitePage
import gr.tech.pdfgenerator.util.BACKEND
import gr.tech.pdfgenerator.util.BACKEND.ROUTES.USER_SIGN_OUT
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.serialization.json.Json
import org.w3c.fetch.INCLUDE
import org.w3c.fetch.RequestCredentials
import org.w3c.fetch.RequestInit
import org.w3c.xhr.FormData
import kotlin.apply
import kotlin.let
import kotlin.text.contains

/*
    Authentication + Authorization
*/

val apiJson = Json { coerceInputValues = true }

/*
    Two Wrapper for all API functions that involve the JWT Cookie's being sent back (credentials).
    Call inside other API functions to ensure the cookie is sent, and if the user is not authenticated,
    they will be redirected to the login page.
 */
suspend inline fun <reified T> safeApiCallStringBody(
    url: String,
    method: String = "GET",
    body: String? = null,
    redirectLogin: Boolean = true,
    redirectTo: String = SitePage.Login.route
): ApiResponse<T> {
    return try {
        val init = RequestInit(
            method = method,
            credentials = RequestCredentials.INCLUDE
        ).apply {
            body?.let { this.body = it }
        }

        val response = window.fetch(url, init).await()
        val jsonText = response.text().await()

        // Handle unauthorized globally
        if (response.status == 401.toShort() &&
            !window.location.href.contains(redirectTo) &&
            redirectLogin
        ) {
            if (UserSession.isLoggedIn()) {
                window.fetch("${BACKEND.BACKEND_URL}${BACKEND.ROUTES.USER_SIGN_OUT}", RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
            }

            window.location.href = redirectTo
            return ApiResponse(
                success = false,
                error = ApiError("unauthorized", "User is not authenticated.")
            )
        }

        apiJson.decodeFromString<ApiResponse<T>>(jsonText)
    } catch (e: Exception) {
        console.error("API call failed: $url", e)
        ApiResponse(
            success = false,
            error = ApiError("network_error", "Failed to connect. Try again later.")
        )
    }
}

suspend inline fun <reified T> safeApiCallFormData(
    url: String,
    method: String = "POST",
    formData: FormData,
    redirectLogin: Boolean = true
): ApiResponse<T> {
    return try {
        val init = RequestInit(
            method = method,
            credentials = RequestCredentials.INCLUDE,
            body = formData
        )

        val response = window.fetch(url, init).await()
        val jsonText = response.text().await()

        // Handle unauthorized globally
        if (response.status == 401.toShort() &&
            !window.location.href.contains(SitePage.Login.route) &&
            redirectLogin
        ) {
            window.location.href = SitePage.Login.route
            return ApiResponse(
                success = false,
                error = ApiError("unauthorized", "User is not authenticated.")
            )
        }

        console.log("Status: ${response.status}")
        val apiResponse = Json.decodeFromString<ApiResponse<T>>(jsonText)
        apiResponse
    } catch (e: Exception) {
        console.error("FormData API call failed", e)
        ApiResponse(
            success = false,
            error = ApiError("network_error", "Failed to connect. Try again later.")
        )
    }
}


suspend fun checkUserJWT(redirectLogin: Boolean = true, redirectTo: String = SitePage.Login.route): ApiResponse<UserPreview> {
    return safeApiCallStringBody(
        url ="${BACKEND.BACKEND_URL}/check-auth",
        redirectLogin = redirectLogin,
        redirectTo = redirectTo
    )
}

suspend fun logoutUser() {
    window.fetch("${BACKEND.BACKEND_URL}${BACKEND.ROUTES.USER_SIGN_OUT}", RequestInit(method = "POST", credentials = RequestCredentials.INCLUDE)).await()
    UserSession.setLoggedOut()
    window.location.href = SitePage.Login.route
}